import { opsOrderActions } from './ops-order-actions';

/** The actions the details page can offer behind its "More" menu. */
export type OrderMoreAction = 'resend_email' | 'resend_whatsapp' | 'order_lifetime' | 'revert_order' | 'view_invoices';

interface OrderHistoryEntry {
  status: string;
}

const RESENDABLE = ['restaurant_placed', 'approved'];

/**
 * The status the order was in before its current one (the latest history
 * entry with a different status), or the current one when there is none.
 */
export function previousOrderStatus(
  currentStatus: string | undefined,
  history: OrderHistoryEntry[] | undefined
): string {
  const entries = history ?? [];
  for (let i = entries.length - 1; i >= 0; i--) {
    if (entries[i].status !== currentStatus) return entries[i].status;
  }
  return currentStatus ?? '';
}

/**
 * Revert is offered only on an order that moved from placed or confirmed to
 * confirmed, received or cancelled — the one step ops may take back.
 */
export function canRevertOrder(
  currentStatus: string | undefined,
  history: OrderHistoryEntry[] | undefined,
  canManage: boolean
): boolean {
  if (!canManage || !currentStatus) return false;
  const previous = previousOrderStatus(currentStatus, history);
  return (
    ['canceled', 'received', 'approved'].includes(currentStatus) &&
    previous !== currentStatus &&
    ['approved', 'restaurant_placed'].includes(previous)
  );
}

export interface OrderDetailActions {
  /** The two decisions ops take, kept as visible buttons. */
  primary: { confirm: boolean; cancel: boolean };
  /** Everything else, in display order, behind the "More" menu. */
  more: OrderMoreAction[];
}

/**
 * What the order details header offers. Takes the RAW status from the API
 * (the screen rewrites `approved`/`rejected` for display) and the facts the
 * rarer actions depend on, so the screen only renders what comes back.
 */
export function orderDetailActions(input: {
  rawStatus: string | undefined;
  history: OrderHistoryEntry[] | undefined;
  canManage: boolean;
  invoiceCount: number;
}): OrderDetailActions {
  const { rawStatus, history, canManage, invoiceCount } = input;
  const { canConfirm, canCancel } = opsOrderActions(rawStatus, canManage);
  const more: OrderMoreAction[] = [];
  if (rawStatus && RESENDABLE.includes(rawStatus)) more.push('resend_email', 'resend_whatsapp');
  more.push('order_lifetime');
  if (canRevertOrder(rawStatus, history, canManage)) more.push('revert_order');
  if (invoiceCount > 0) more.push('view_invoices');
  return { primary: { confirm: canConfirm, cancel: canCancel }, more };
}
